import { useState, useEffect, useCallback } from "react";
import { IconX, IconShield } from "./Icons";

/* ──────────────────────────── Types ──────────────────────────── */

interface OrderSummary {
  orderId: string;
  status: "pending" | "success" | "failed";
  courseId: string | null;
  amount: number;
  currency: string;
  buyerName: string;
  buyerEmail: string;
  createdAt: string;
  updatedAt: string;
}

interface OrderDetail extends OrderSummary {
  installmentCount: number;
  basketItemName: string;
  buyer: {
    name: string;
    surName: string;
    emailAddress: string;
    phoneNumber: string;
    identityNumber: string | null;
  };
  billingAddress: {
    address: string;
    city: string;
    district: string;
    zipCode: string;
    country: string;
    companyName: string;
    taxOffice: string;
    taxNumber: string;
  };
  paymentMeta?: {
    cardBrand?: string;
    maskedNumber?: string;
    cardType?: string;
    installmentCount?: number;
  };
}

type StatusFilter = "all" | "success" | "failed" | "pending";

/* ──────────────────────────── Helpers ──────────────────────────── */

const STATUS_LABELS: Record<string, string> = {
  success: "Başarılı",
  failed: "Başarısız",
  pending: "Beklemede",
};

const STATUS_COLORS: Record<string, string> = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
};

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full border ${
        STATUS_COLORS[status] || "bg-gray-50 text-gray-600 border-gray-200"
      }`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

/* ──────────────────────────── API ──────────────────────────── */

async function loginAdmin(password: string): Promise<string> {
  const res = await fetch("/.netlify/functions/admin-auth", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  const data = await res.json();
  if (!res.ok || !data.token) throw new Error(data.error || "Giriş başarısız.");
  return data.token;
}

async function fetchOrders(token: string): Promise<OrderSummary[]> {
  const res = await fetch("/.netlify/functions/admin-orders", {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error("UNAUTHORIZED");
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Siparişler yüklenemedi.");
  return data.orders;
}

async function fetchOrderDetail(token: string, id: string): Promise<OrderDetail> {
  const res = await fetch(`/.netlify/functions/admin-orders?id=${encodeURIComponent(id)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error("UNAUTHORIZED");
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Sipariş detayı yüklenemedi.");
  return data;
}

/* ──────────────────────────── Login Screen ──────────────────────────── */

function LoginScreen({ onLogin }: { onLogin: (token: string) => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;
    setError(null);
    setLoading(true);
    try {
      const token = await loginAdmin(password);
      sessionStorage.setItem("haptone-admin-token", token);
      onLogin(token);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-light flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="mx-auto w-14 h-14 bg-primary rounded-2xl flex items-center justify-center mb-4">
            <IconShield size={24} className="text-accent" />
          </div>
          <h1 className="text-2xl font-bold text-primary">Admin Paneli</h1>
          <p className="mt-1 text-muted text-sm">Devam etmek için giriş yapın</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-border p-6 shadow-sm space-y-4">
          <div>
            <label htmlFor="admin-password" className="block text-sm font-semibold text-primary mb-1.5">
              Şifre
            </label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin şifresini girin"
              autoFocus
              className="w-full rounded-xl border border-border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
            />
          </div>

          {error && (
            <p className="text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-light disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300"
          >
            {loading ? "Giriş yapılıyor…" : "Giriş Yap"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ──────────────────────────── Order Detail Modal ──────────────────────────── */

function OrderDetailModal({
  order,
  onClose,
}: {
  order: OrderDetail;
  onClose: () => void;
}) {
  const buyer = order.buyer;
  const billing = order.billingAddress;
  const meta = order.paymentMeta;

  const sections = [
    {
      title: "Müşteri Bilgileri",
      rows: [
        ["Ad Soyad", `${buyer.name} ${buyer.surName}`],
        ["E-posta", buyer.emailAddress],
        ["Telefon", buyer.phoneNumber],
        ...(buyer.identityNumber ? [["TC Kimlik No", buyer.identityNumber]] : []),
      ],
    },
    {
      title: "Fatura Adresi",
      rows: [
        ["Adres", billing.address],
        ["Şehir", billing.city],
        ...(billing.district ? [["İlçe", billing.district]] : []),
        ...(billing.zipCode ? [["Posta Kodu", billing.zipCode]] : []),
        ["Ülke", billing.country],
        ...(billing.companyName ? [["Firma", billing.companyName]] : []),
        ...(billing.taxOffice ? [["Vergi Dairesi", billing.taxOffice]] : []),
        ...(billing.taxNumber ? [["Vergi No", billing.taxNumber]] : []),
      ],
    },
    {
      title: "Ödeme Bilgileri",
      rows: [
        ["Tutar", formatCurrency(order.amount, order.currency)],
        ["Durum", null],
        ["Taksit", String(meta?.installmentCount || order.installmentCount || 1)],
        ...(meta?.cardBrand ? [["Kart Markası", meta.cardBrand]] : []),
        ...(meta?.maskedNumber ? [["Kart No", meta.maskedNumber]] : []),
        ...(meta?.cardType ? [["Kart Tipi", meta.cardType]] : []),
        ["Sipariş ID", order.orderId],
        ["Oluşturulma", formatDate(order.createdAt)],
        ["Son Güncelleme", formatDate(order.updatedAt)],
      ],
    },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-primary/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          aria-label="Kapat"
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-lg text-muted hover:bg-light hover:text-primary transition-colors"
        >
          <IconX size={20} />
        </button>

        <h3 className="text-xl font-bold text-primary mb-1">Sipariş Detayı</h3>
        <p className="text-sm text-muted mb-6">{order.basketItemName || order.courseId || "—"}</p>

        <div className="space-y-6">
          {sections.map((section) => (
            <div key={section.title}>
              <h4 className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">
                {section.title}
              </h4>
              <div className="bg-light rounded-xl border border-border divide-y divide-border">
                {section.rows.map(([label, value]) => (
                  <div key={label as string} className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-muted">{label}</span>
                    {value === null ? (
                      <StatusBadge status={order.status} />
                    ) : (
                      <span className="text-sm font-medium text-primary text-right max-w-[60%] break-words">
                        {value}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────── Admin Dashboard ──────────────────────────── */

function Dashboard({
  token,
  onLogout,
}: {
  token: string;
  onLogout: () => void;
}) {
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchOrders(token);
      setOrders(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.message === "UNAUTHORIZED") {
        onLogout();
        return;
      }
      setError(err instanceof Error ? err.message : "Bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleOrderClick = async (orderId: string) => {
    setDetailLoading(true);
    try {
      const detail = await fetchOrderDetail(token, orderId);
      setSelectedOrder(detail);
    } catch (err: unknown) {
      if (err instanceof Error && err.message === "UNAUTHORIZED") {
        onLogout();
        return;
      }
      alert(err instanceof Error ? err.message : "Detay yüklenemedi.");
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredOrders = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  const stats = {
    total: orders.length,
    success: orders.filter((o) => o.status === "success").length,
    failed: orders.filter((o) => o.status === "failed").length,
    pending: orders.filter((o) => o.status === "pending").length,
  };

  return (
    <div className="min-h-screen bg-light">
      {/* HEADER */}
      <header className="sticky top-0 z-40 bg-white border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <IconShield size={16} className="text-accent" />
            </div>
            <h1 className="text-lg font-bold text-primary">Sipariş Yönetimi</h1>
          </div>
          <button
            onClick={onLogout}
            className="text-sm font-semibold text-muted hover:text-primary transition-colors"
          >
            Çıkış Yap
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* STATS */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Toplam", value: stats.total, color: "bg-primary text-white" },
            { label: "Başarılı", value: stats.success, color: "bg-emerald-500 text-white" },
            { label: "Başarısız", value: stats.failed, color: "bg-red-500 text-white" },
            { label: "Beklemede", value: stats.pending, color: "bg-amber-500 text-white" },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white rounded-xl border border-border p-4">
              <div className="text-xs font-semibold uppercase tracking-widest text-muted mb-1">{label}</div>
              <div className="flex items-center gap-2">
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${color}`}>
                  {value}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* FILTER */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex gap-2">
            {(["all", "success", "failed", "pending"] as StatusFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                  filter === f
                    ? "bg-accent text-white border-accent"
                    : "bg-white text-muted border-border hover:border-accent/40"
                }`}
              >
                {f === "all" ? "Tümü" : STATUS_LABELS[f]}
              </button>
            ))}
          </div>
          <button
            onClick={loadOrders}
            className="text-xs font-semibold text-accent hover:text-accent-light transition-colors"
          >
            ↻ Yenile
          </button>
        </div>

        {/* TABLE */}
        {loading ? (
          <div className="text-center py-16 text-muted">Yükleniyor…</div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-red-600 font-medium mb-3">{error}</p>
            <button onClick={loadOrders} className="text-sm font-semibold text-accent">
              Tekrar Dene
            </button>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-16 text-muted">
            {orders.length === 0 ? "Henüz sipariş bulunmuyor." : "Bu filtreyle eşleşen sipariş yok."}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-border overflow-hidden">
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-light border-b border-border">
                    <th className="text-left px-4 py-3 font-semibold text-primary">Tarih</th>
                    <th className="text-left px-4 py-3 font-semibold text-primary">Müşteri</th>
                    <th className="text-left px-4 py-3 font-semibold text-primary">E-posta</th>
                    <th className="text-right px-4 py-3 font-semibold text-primary">Tutar</th>
                    <th className="text-center px-4 py-3 font-semibold text-primary">Durum</th>
                    <th className="text-right px-4 py-3 font-semibold text-primary">Detay</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredOrders.map((order) => (
                    <tr key={order.orderId} className="hover:bg-light/50 transition-colors">
                      <td className="px-4 py-3 text-muted whitespace-nowrap">{formatDate(order.createdAt)}</td>
                      <td className="px-4 py-3 font-medium text-primary">{order.buyerName}</td>
                      <td className="px-4 py-3 text-muted">{order.buyerEmail}</td>
                      <td className="px-4 py-3 text-right font-semibold text-primary whitespace-nowrap">
                        {formatCurrency(order.amount, order.currency)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge status={order.status} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleOrderClick(order.orderId)}
                          disabled={detailLoading}
                          className="text-accent hover:text-accent-light font-semibold text-xs transition-colors"
                        >
                          Görüntüle
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-border">
              {filteredOrders.map((order) => (
                <button
                  key={order.orderId}
                  onClick={() => handleOrderClick(order.orderId)}
                  className="w-full text-left p-4 hover:bg-light/50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-primary text-sm">{order.buyerName}</span>
                    <StatusBadge status={order.status} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted">{formatDate(order.createdAt)}</span>
                    <span className="text-sm font-semibold text-primary">
                      {formatCurrency(order.amount, order.currency)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      {selectedOrder && (
        <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}
    </div>
  );
}

/* ──────────────────────────── Root ──────────────────────────── */

export default function AdminPage() {
  const [token, setToken] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return sessionStorage.getItem("haptone-admin-token");
  });

  const handleLogout = () => {
    sessionStorage.removeItem("haptone-admin-token");
    setToken(null);
  };

  if (!token) {
    return <LoginScreen onLogin={setToken} />;
  }

  return <Dashboard token={token} onLogout={handleLogout} />;
}
